// Apolo IDE — TaskNode (S2 Task Layer E2-WS1-T02).
//
// Single task node for the DAG Board. Rendered inside an SVG foreignObject so
// it can use HTML/CSS while the board draws SVG edges around it.
// Styling: rx-* tokens scoped to .rx-shell (S1 baseline).

import { Cpu, CheckCircle, XCircle, Loader, Clock, Play } from 'lucide-react';
import { TaskOracleStatus } from './TaskOracleStatus.jsx';

const STATUS_META = {
  proposed: { label: 'Aguardando', color: 'var(--rx-tx3)', bg: 'var(--rx-s2)', Icon: Clock },
  approved:  { label: 'Aprovada',   color: 'var(--rx-accent)', bg: 'var(--rx-s2)', Icon: Play },
  running:   { label: 'Executando', color: 'var(--rx-gold)',   bg: 'var(--rx-s2)', Icon: Loader },
  done:      { label: 'Concluída',  color: 'var(--rx-add)',    bg: 'var(--rx-s2)', Icon: CheckCircle },
  failed:    { label: 'Falhou',     color: 'var(--rx-del)',    bg: 'var(--rx-s2)', Icon: XCircle },
};

const MODEL_SHORT = (m) => {
  if (!m) return '?';
  const parts = m.split('/');
  const last = parts[parts.length - 1] ?? m;
  return last.length > 16 ? last.slice(0, 14) + '…' : last;
};

const truncate = (s, n = 60) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s ?? '');

/**
 * @param {{
 *   task: import('../../../lib/taskTypes.js').Task
 *   verifyResult?: { reachedGreen: boolean, noVerifiers: boolean, failed: Array<{verifier:string, evidence:string}> }
 *   selected?: boolean
 *   onClick?: () => void
 * }} props
 */
export function TaskNode({ task, verifyResult, selected = false, onClick }) {
  const meta = STATUS_META[task.status] ?? STATUS_META.proposed;
  const { Icon } = meta;
  const isSpinning = task.status === 'running';

  return (
    <div
      data-testid="task-node"
      data-task-id={task.id}
      data-status={task.status}
      role="button"
      tabIndex={0}
      aria-label={`Task: ${task.goal.slice(0, 80)}, status ${meta.label}`}
      aria-pressed={selected}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
      style={{
        width: '100%',
        height: '100%',
        background: 'var(--rx-s2)',
        border: `1px solid ${selected ? 'var(--rx-accent)' : 'var(--rx-border)'}`,
        borderRadius: 10,
        padding: '8px 10px',
        boxSizing: 'border-box',
        cursor: 'pointer',
        outline: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        boxShadow: selected ? '0 0 0 2px var(--rx-accent)' : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
    >
      {/* Status row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon
          size={13}
          aria-hidden="true"
          style={{
            color: meta.color,
            flexShrink: 0,
            animation: isSpinning ? 'spin 1.2s linear infinite' : 'none',
          }}
        />
        <span style={{ fontSize: 10, color: meta.color, fontWeight: 600, letterSpacing: '0.04em' }}>
          {meta.label}
        </span>
        {task.canParallelize && (
          <span
            aria-label="pode executar em paralelo"
            style={{
              marginLeft: 'auto',
              fontSize: 9,
              color: 'var(--rx-cyan)',
              background: 'rgba(34,211,238,.12)',
              border: '1px solid rgba(34,211,238,.25)',
              borderRadius: 999,
              padding: '1px 5px',
              letterSpacing: '0.04em',
            }}
          >
            ∥
          </span>
        )}
      </div>

      {/* Goal */}
      <p style={{
        margin: 0,
        fontSize: 11,
        color: 'var(--rx-tx1)',
        lineHeight: 1.4,
        wordBreak: 'break-word',
      }}>
        {truncate(task.goal, 72)}
      </p>

      {/* Model chip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
        <Cpu size={10} aria-hidden="true" style={{ color: 'var(--rx-tx4)', flexShrink: 0 }} />
        <span style={{ fontSize: 10, color: 'var(--rx-tx3)', fontFamily: 'var(--rx-mono)' }}>
          {MODEL_SHORT(task.model)}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--rx-tx4)' }}>
          ${task.estimatedCostUsd.toFixed(3)}
        </span>
      </div>

      {/* Oracle status (when verifyResult provided) */}
      {verifyResult && !verifyResult.noVerifiers && (
        <div data-testid="task-oracle-inline" style={{ marginTop: 4 }}>
          <TaskOracleStatus verifyResult={verifyResult} compact />
        </div>
      )}
    </div>
  );
}
